/**
 * Printora Gang Builder — MaxRects + shelf nesting (fixed 300mm width).
 */
(() => {
  'use strict';
  const GB = () => window.PrintoraGangBuilder;

  function expandItems(items) {
    const out = [];
    items.forEach((item) => {
      const qty = Math.max(1, Number(item.qty) || 1);
      for (let i = 0; i < qty; i += 1) {
        out.push({
          ...item,
          instanceId: `${item.id}-${i}`,
        });
      }
    });
    return out;
  }

  function sortStrategies(items) {
    return [
      [...items].sort((a, b) => b.widthMm * b.heightMm - a.widthMm * a.heightMm),
      [...items].sort((a, b) => Math.max(b.widthMm, b.heightMm) - Math.max(a.widthMm, a.heightMm)),
      [...items].sort((a, b) => b.widthMm - a.widthMm),
      [...items].sort((a, b) => b.heightMm - a.heightMm),
      [...items],
    ];
  }

  function orientations(item, sheetWidth, margin) {
    const maxW = sheetWidth - margin * 2;
    const opts = [];
    const add = (w, h, rot) => {
      if (w <= maxW && w > 0 && h > 0) opts.push({ widthMm: w, heightMm: h, rotationDeg: rot ? 90 : 0 });
    };
    add(item.widthMm, item.heightMm, false);
    if (item.allowAutoRotate !== false && item.widthMm !== item.heightMm) {
      add(item.heightMm, item.widthMm, true);
    }
    return opts;
  }

  /** MaxRects bin packer */
  class MaxRectsBin {
    constructor(width, height) {
      this.width = width;
      this.height = height;
      this.freeRects = [{ x: 0, y: 0, width, height }];
      this.usedRects = [];
    }

    scoreRect(rect, w, h) {
      const leftoverHoriz = rect.width - w;
      const leftoverVert = rect.height - h;
      const shortSide = Math.min(leftoverHoriz, leftoverVert);
      const longSide = Math.max(leftoverHoriz, leftoverVert);
      return { shortSide, longSide, areaFit: rect.width * rect.height - w * h };
    }

    findPosition(w, h) {
      let best = null;
      this.freeRects.forEach((rect) => {
        if (w <= rect.width && h <= rect.height) {
          const score = this.scoreRect(rect, w, h);
          if (!best || score.shortSide < best.score.shortSide
            || (score.shortSide === best.score.shortSide && score.longSide < best.score.longSide)) {
            best = { x: rect.x, y: rect.y, score };
          }
        }
      });
      return best;
    }

    placeRect(x, y, w, h) {
      this.usedRects.push({ x, y, width: w, height: h });
      const nextFree = [];
      this.freeRects.forEach((free) => {
        if (x >= free.x + free.width || x + w <= free.x
          || y >= free.y + free.height || y + h <= free.y) {
          nextFree.push(free);
          return;
        }
        if (x > free.x) nextFree.push({ x: free.x, y: free.y, width: x - free.x, height: free.height });
        if (x + w < free.x + free.width) {
          nextFree.push({
            x: x + w, y: free.y, width: free.x + free.width - x - w, height: free.height,
          });
        }
        if (y > free.y) nextFree.push({ x: free.x, y: free.y, width: free.width, height: y - free.y });
        if (y + h < free.y + free.height) {
          nextFree.push({
            x: free.x, y: y + h, width: free.width, height: free.y + free.height - y - h,
          });
        }
      });
      this.freeRects = nextFree.filter((r) => r.width > 0.5 && r.height > 0.5);
    }

    getUsedHeight() {
      if (!this.usedRects.length) return 0;
      return Math.max(...this.usedRects.map((r) => r.y + r.height));
    }
  }

  function maxRectsPack(sorted, options) {
    const sheetWidth = options.sheetWidthMm;
    const gap = options.gapMm;
    const margin = options.marginMm;
    const innerW = sheetWidth - margin * 2;
    const estimateH = sorted.reduce((s, i) => s + Math.max(i.widthMm, i.heightMm) + gap, margin * 2);
    const bin = new MaxRectsBin(innerW, estimateH);
    const placements = [];

    for (const item of sorted) {
      let placed = false;
      let best = null;
      orientations(item, sheetWidth, margin).forEach((o) => {
        const pw = o.widthMm + gap;
        const ph = o.heightMm + gap;
        const pos = bin.findPosition(pw, ph);
        if (pos && (!best || pos.y + ph < best.y + best.ph
          || (pos.y + ph === best.y + best.ph && pos.x < best.x))) {
          best = { ...pos, w: o.widthMm, h: o.heightMm, rot: o.rotationDeg === 90, pw, ph };
        }
      });
      if (!best) {
        for (const o of orientations(item, sheetWidth, margin)) {
          if (o.widthMm > innerW) return { error: `"${item.name}" exceeds ${sheetWidth}mm width.`, placements };
        }
        return { error: `Could not place "${item.name}".`, placements };
      }
      bin.placeRect(best.x, best.y, best.pw, best.ph);
      placements.push({
        id: item.id,
        instanceId: item.instanceId,
        assetId: item.id,
        name: item.name,
        xMm: GB().roundMm(margin + best.x),
        yMm: GB().roundMm(margin + best.y),
        widthMm: GB().roundMm(best.w),
        heightMm: GB().roundMm(best.h),
        rotationDeg: 0,
      });
      placed = true;
      if (!placed) return { error: `Could not place "${item.name}".`, placements };
    }
    return { placements };
  }

  /** Skyline / shelf packer — fast baseline */
  function shelfPack(sorted, options) {
    const sheetWidth = options.sheetWidthMm;
    const gap = options.gapMm;
    const margin = options.marginMm;
    const placements = [];
    let shelfY = margin;
    let shelfX = margin;
    let shelfH = 0;

    const place = (item, w, h, rot) => {
      placements.push({
        id: item.id,
        instanceId: item.instanceId,
        assetId: item.id,
        name: item.name,
        xMm: GB().roundMm(shelfX),
        yMm: GB().roundMm(shelfY),
        widthMm: GB().roundMm(w),
        heightMm: GB().roundMm(h),
        rotationDeg: 0,
      });
      shelfX += w + gap;
      shelfH = Math.max(shelfH, h);
    };

    for (const item of sorted) {
      let placed = false;
      for (const o of orientations(item, sheetWidth, margin)) {
        if (shelfX + o.widthMm <= sheetWidth - margin + 0.01) {
          place(item, o.widthMm, o.heightMm, o.rotationDeg === 90);
          placed = true;
          break;
        }
      }
      if (placed) continue;
      shelfY += shelfH + gap;
      shelfX = margin;
      shelfH = 0;
      for (const o of orientations(item, sheetWidth, margin)) {
        if (o.widthMm > sheetWidth - margin * 2) {
          return { error: `"${item.name}" exceeds ${sheetWidth}mm width.`, placements };
        }
        if (shelfX + o.widthMm <= sheetWidth - margin + 0.01) {
          place(item, o.widthMm, o.heightMm, o.rotationDeg === 90);
          placed = true;
          break;
        }
      }
      if (!placed) return { error: `Could not place "${item.name}".`, placements };
    }
    return { placements };
  }

  const PACKERS = [maxRectsPack, shelfPack];

  /** Nest items already on the sheet — keeps each item's width/height, only updates positions. */
  function nestItemsInPlace(items, options = {}) {
    const config = { ...GB().DEFAULT_CONFIG, ...options };
    const sheetWidth = config.sheetWidthMm;
    const gap = config.gapMm ?? config.defaultSpacingMm;
    const margin = config.marginMm ?? config.safeMarginMm ?? 0;
    const prepared = items.map((item) => ({ ...item, allowAutoRotate: false }));
    if (!prepared.length) {
      return {
        placements: [],
        usedLengthMm: config.defaultSheetLengthMm || 1000,
        sheetWidthMm: sheetWidth,
        designCount: 0,
        efficiency: 0,
      };
    }

    let best = null;
    let bestLen = Infinity;

    sortStrategies(prepared).forEach((sorted) => {
      PACKERS.forEach((packer) => {
        const result = packer(sorted, { sheetWidthMm: sheetWidth, gapMm: gap, marginMm: margin });
        if (result.error) return;
        const len = GB().usedLengthMm(result.placements, margin);
        if (len < bestLen) {
          bestLen = len;
          best = result.placements;
        }
      });
    });

    if (!best) {
      const fallback = shelfPack(prepared, { sheetWidthMm: sheetWidth, gapMm: gap, marginMm: margin });
      if (fallback.error) return { ...fallback, placements: fallback.placements || [], usedLengthMm: 0 };
      best = fallback.placements;
      bestLen = GB().usedLengthMm(best, margin);
    }

    const minLen = config.defaultSheetLengthMm || 1000;
    const usedLength = GB().roundMm(Math.max(bestLen + margin, minLen));
    const efficiency = GB().utilisation(
      best.map((p) => ({ widthMm: p.widthMm, heightMm: p.heightMm })),
      sheetWidth,
      usedLength
    );

    return {
      placements: best,
      usedLengthMm: usedLength,
      sheetWidthMm: sheetWidth,
      designCount: best.length,
      efficiency,
    };
  }

  function nestItems(items, options = {}) {
    const config = { ...GB().DEFAULT_CONFIG, ...options };
    const sheetWidth = config.sheetWidthMm;
    const gap = config.gapMm ?? config.defaultSpacingMm;
    const margin = config.marginMm ?? config.safeMarginMm;
    const expanded = expandItems(items);
    if (!expanded.length) {
      return {
        placements: [],
        usedLengthMm: config.defaultSheetLengthMm || 1000,
        sheetWidthMm: sheetWidth,
        designCount: 0,
        efficiency: 0,
      };
    }

    let best = null;
    let bestLen = Infinity;

    sortStrategies(expanded).forEach((sorted) => {
      PACKERS.forEach((packer) => {
        const result = packer(sorted, { sheetWidthMm: sheetWidth, gapMm: gap, marginMm: margin });
        if (result.error) return;
        const len = GB().usedLengthMm(result.placements, margin);
        if (len < bestLen) {
          bestLen = len;
          best = result.placements;
        }
      });
    });

    if (!best) {
      const fallback = shelfPack(expanded, { sheetWidthMm: sheetWidth, gapMm: gap, marginMm: margin });
      if (fallback.error) return { ...fallback, placements: fallback.placements || [], usedLengthMm: 0 };
      best = fallback.placements;
      bestLen = GB().usedLengthMm(best, margin);
    }

    const minLen = config.defaultSheetLengthMm || 1000;
    const usedLength = GB().roundMm(Math.max(bestLen + margin, minLen));
    const efficiency = GB().utilisation(
      best.map((p) => ({ widthMm: p.widthMm, heightMm: p.heightMm })),
      sheetWidth,
      usedLength
    );

    return {
      placements: best,
      usedLengthMm: usedLength,
      sheetWidthMm: sheetWidth,
      designCount: best.length,
      efficiency,
    };
  }

  window.PrintoraGangBuilderNest = { nestItems, nestItemsInPlace, expandItems, maxRectsPack, shelfPack };
  if (window.PrintoraGangSheet) window.PrintoraGangSheet.nestItems = nestItems;
})();
